import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { Form as FormikForm, InputField } from "components/common_components/Formik";
import { DataSearchDialog } from "components/common_components/Dialogs";
import Grid from "@material-ui/core/Grid";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import InvoicingContext from "./InvoicingContext";
import { dateToString } from "utils/dateUtils";
import Money from "components/common_components/Money";

function Form({ invoice, resources, mode, ...rest }) {
    const adding = mode === "add";
    const updating = mode === "update";
    const { Customers } = useContext(InvoicingContext);

    const getInitialValues = () => {
        if (adding || !invoice) {
            return {
                id: null,
                client: null,
            };
        }

        const { clientId, clientName, clientAddress, ...restInvoice } = invoice;

        return {
            ...restInvoice,
            client: {
                id: clientId,
                fullName: clientName,
                fullAddress: clientAddress,
            },
        };
    };

    function filterClientsBuilder(filterValue) {
        return filterValue
            ? {
                  firstName: filterValue,
                  lastName: filterValue,
                  middleName: filterValue,
                  homePhone: filterValue,
                  ssn: filterValue,
              }
            : {};
    }

    const [searchClientDialogProps, setSearchClientDialogProps] = useState({
        open: false,
        title: "Select a client",
        onClose: () => setSearchClientDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        onSecondaryClick: undefined,
        tableProps: {
            defaultOrder: { order: "asc", orderBy: "firstName" },
            keyColumnName: "id",
            selectable: true,
            allowSelectAll: false,
            allowMultiSelect: false,
            dense: false,
            deletable: false,
            onRequestData: Customers.getListAsync,
            filterBuilder: filterClientsBuilder,
        },
    });

    function setCustomer(selectedItems, { values: currentValues, setValues }) {
        const client = selectedItems[0];
        if (client) {
            setValues({
                ...currentValues,
                client,
            });
        }
    }

    function searchCustomer(form) {
        const headCells = [
            { index: 1, id: "firstName", disablePadding: false, label: "First Name" },
            { index: 2, id: "lastName", disablePadding: false, label: "Last Name" },
            { index: 3, id: "homePhone", disablePadding: true, label: "Home Phone" },
            { index: 4, id: "birthDateString", disablePadding: true, label: "Date of Birth" },
            { index: 5, id: "ssn", disablePadding: true, label: "SSN" },
        ];
        setSearchClientDialogProps((prev) => ({
            ...prev,
            open: true,
            onPrimaryClick: (selectedItems) => setCustomer(selectedItems, form),
            tableProps: {
                headCells,
                ...prev.tableProps,
            },
        }));
    }

    return (
        <div className="layout">
            <FormikForm validateOnChange={false} initialValues={getInitialValues()} showButtons={false} {...rest}>
                {(form) => (
                    <>
                        <Grid item>
                            <Grid container direction="row" spacing={2} alignItems="flex-start">
                                <Grid item xs={12}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={3}>
                                            <InputField disabled fieldName="client.fullName" label="Client" />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <InputField disabled fieldName="client.fullAddress" label="Address" />
                                        </Grid>
                                        <Grid item>
                                            {mode === "add" && (
                                                <Button secondary outline onClick={() => searchCustomer(form)}>
                                                    <Icon name="search" size="xs" color="secondary" />
                                                    <span>Search</span>
                                                </Button>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {updating && (
                                    <>
                                        <Grid item xs={12} md={4}>
                                            <Grid container direction="column">
                                                <Grid item>
                                                    <strong>Issued by: </strong>
                                                    <small>{`${form.values.issuer?.name}`}</small>
                                                </Grid>
                                                <Grid item>
                                                    <strong>Issued on: </strong>
                                                    <small>{`${form.values.issueDateString}`}</small>
                                                </Grid>
                                                <Grid item>
                                                    <strong>Issued at: </strong>
                                                    <small>{`${form.values.issuer?.address}`}</small>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Grid container direction="column">
                                                <Grid item>
                                                    <strong>Generated by: </strong>
                                                    <small>{`${form.values.stampCreationUser?.name}`}</small>
                                                </Grid>
                                                <Grid item>
                                                    <strong>Generated on: </strong>
                                                    <small>{`${dateToString(form.values.stampCreationDatetime)}`}</small>
                                                </Grid>
                                                <Grid item>
                                                    <strong>Modified by: </strong>
                                                    <small>{`${form.values.stampModificationUser?.name}`}</small>
                                                </Grid>
                                                <Grid item>
                                                    <strong>Modified on: </strong>
                                                    <small>{`${dateToString(form.values.stampModificationDatetime)}`}</small>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Grid container direction="column" justifyContent="center" alignItems="center">
                                                <Grid item>
                                                    <h2>Total</h2>
                                                </Grid>
                                                <Grid item>
                                                    <h2>
                                                        <Money amount={form.values.totalAmount} />
                                                    </h2>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        {adding && (
                            <Grid item>
                                <Grid container spacing={2}>
                                    <Grid item>
                                        <Button secondary outline onClick={rest.onCancel}>
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button secondary type="submit" loading={form.isSubmitting}>
                                            Create
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </>
                )}
            </FormikForm>
            <DataSearchDialog {...searchClientDialogProps} />
        </div>
    );
}

Form.propTypes = {
    invoice: PropTypes.shape({
        id: PropTypes.number,
        issueDate: PropTypes.instanceOf(Date),
        issueDateString: PropTypes.string,
        consecutive: PropTypes.number,
        totalAmount: PropTypes.number,
        paymentDueDate: PropTypes.instanceOf(Date),
        issuerId: PropTypes.number,
        clientId: PropTypes.number,
        clientName: PropTypes.string,
        clientAddress: PropTypes.string,
        module: PropTypes.string,
        fileCaseId: PropTypes.number,
        accountingId: PropTypes.number,
        stampCreationDatetime: PropTypes.instanceOf(Date),
        stampCreationUser: PropTypes.shape({
            name: PropTypes.string,
        }),
        stampModificationDatetime: PropTypes.instanceOf(Date),
        stampModificationUser: PropTypes.shape({
            name: PropTypes.string,
        }),
        issuer: PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            address: PropTypes.string,
            phoneNumber: PropTypes.string,
            email: PropTypes.string,
            fax: PropTypes.string,
        }).isRequired,
    }),
    mode: PropTypes.oneOf(["add", "update"]).isRequired,
};

Form.defaultProps = {
    invoice: undefined,
};

export default Form;
